import { Tabs, useRouter, useSegments } from "expo-router";
import { View, Text, Modal, Platform, useWindowDimensions } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { useAuth } from "@/providers/AuthProvider";
import { useTheme, useThemeColors } from "@/providers/ThemeProvider";
import { useUserChallenges } from "@/modules/challenges";
import { useChats } from "@/modules/chat";
import { 
  CelebrationModal, 
  NotificationsCarousel,
  useNotificationRealtime 
} from "@/modules/notifications";
import {
  OnboardingCarousel,
  USER_ONBOARDING_SLIDES,
  USER_ONBOARDING_BUTTON_TEXT,
  CompleteProfileScreen,
} from "@/modules/onboarding";

/**
 * Main tab navigation layout
 * Shows different tabs based on user roles
 */
export default function TabsLayout() {
  const insets = useSafeAreaInsets();
  const { height: screenHeight, width: screenWidth } = useWindowDimensions();
  const isLargeAndroid = Platform.OS === "android" && screenHeight / screenWidth > 2;
  const router = useRouter();
  const segments = useSegments();
  const { 
    roles, 
    user, 
    activeProfile, 
    needsOnboarding, 
    needsProfileCompletion,
    completeOnboarding,
    completeProfileSetup,
    refreshProfile,
  } = useAuth();
  const { isDark } = useTheme();
  const colors = useThemeColors();
  const { badgeCount } = useUserChallenges();
  const { getTotalUnreadCount } = useChats(activeProfile?.id || user?.id);
  const unreadChatCount = getTotalUnreadCount();

  // Subscribe to realtime notifications
  const {
    currentNotification,
    showCelebration,
    dismissCelebration,
    showCarousel,
    dismissCarousel,
    unreadNotifications,
    markAsRead,
    markAllAsRead,
  } = useNotificationRealtime({
    autoShowCelebration: true,
  });

  // Check if user has admin privileges
  const isAdmin = roles.isFamilieAdmin || roles.isSuperAdmin;

  /**
   * Handle tab press - pop to root if already on that tab
   */
  const handleTabPress = (tabName: string, defaultPath: string) => {
    // Check if we're currently in a nested screen within this tab
    const currentTab = segments[1]; // e.g., "games", "profile", etc.
    const hasNestedScreen = segments.length > 2;
    
    if (currentTab === tabName && hasNestedScreen) {
      // We're in a nested screen of this tab, pop to root
      router.replace(defaultPath as any);
      return true; // Prevent default behavior
    }
    return false; // Allow default behavior
  };

  return (
    <>
    <Tabs
      screenOptions={{
        tabBarActiveTintColor: colors.primary,
        tabBarInactiveTintColor: colors.textTertiary,
        tabBarHideOnKeyboard: true,
        tabBarStyle: {
          backgroundColor: colors.surface,
          borderTopWidth: 1,
          borderTopColor: colors.border,
          paddingTop: 8,
          paddingBottom: Math.max(insets.bottom, 8) + (Platform.OS === "android" ? 2 : 0),
          height: 65 + Math.max(insets.bottom - 6, 0) + (Platform.OS === "android" ? 2 : 0) + (isLargeAndroid ? 5 : 0),
        },
        tabBarLabelStyle: {
          fontSize: 12,
          fontWeight: "500",
          marginTop: 4,
          marginBottom: isLargeAndroid ? 4 : 2,
        },
        headerShown: false,
      }}
    >
      <Tabs.Screen
        name="index"
        options={{
          title: "Home",
          tabBarIcon: ({ color, size }) => (
            <Ionicons name="home-outline" size={size} color={color} />
          ),
        }}
      />
      <Tabs.Screen
        name="games"
        options={{
          title: "Spellen",
          tabBarIcon: ({ color, size }) => (
            <View>
              <Ionicons name="game-controller-outline" size={size} color={color} />
              {badgeCount > 0 && (
                <View
                  style={{
                    position: "absolute",
                    top: -4,
                    right: -8,
                    backgroundColor: "#EF4444",
                    borderRadius: 10,
                    minWidth: 18,
                    height: 18,
                    alignItems: "center",
                    justifyContent: "center",
                    paddingHorizontal: 4,
                  }}
                >
                  <Text style={{ color: "#FFFFFF", fontSize: 10, fontWeight: "bold" }}>
                    {badgeCount > 9 ? "9+" : badgeCount}
                  </Text>
                </View>
              )}
            </View>
          ),
        }}
        listeners={{
          tabPress: (e) => {
            if (handleTabPress("games", "/(tabs)/games")) {
              e.preventDefault();
            }
          },
        }}
      />
      <Tabs.Screen
        name="chat"
        options={{
          title: "Chat",
          tabBarIcon: ({ color, size }) => (
            <View>
              <Ionicons name="chatbubbles-outline" size={size} color={color} />
              {unreadChatCount > 0 && (
                <View
                  style={{
                    position: "absolute",
                    top: -4,
                    right: -8,
                    backgroundColor: "#10B981",
                    borderRadius: 10,
                    minWidth: 18,
                    height: 18,
                    alignItems: "center",
                    justifyContent: "center",
                    paddingHorizontal: 4,
                  }}
                >
                  <Text style={{ color: "#FFFFFF", fontSize: 10, fontWeight: "bold" }}>
                    {unreadChatCount > 99 ? "99+" : unreadChatCount}
                  </Text>
                </View>
              )}
            </View>
          ),
        }}
        listeners={{
          tabPress: (e) => {
            // Chat is now a single screen, no nested navigation
          },
        }}
      />
      <Tabs.Screen
        name="teams"
        options={{
          title: "Teams",
          tabBarIcon: ({ color, size }) => (
            <Ionicons name="people-outline" size={size} color={color} />
          ),
        }}
        listeners={{
          tabPress: (e) => {
            if (handleTabPress("teams", "/(tabs)/teams")) {
              e.preventDefault();
            }
          },
        }}
      />
      <Tabs.Screen
        name="profile"
        options={{
          title: "Profiel",
          tabBarIcon: ({ color, size }) => (
            <Ionicons name="person-outline" size={size} color={color} />
          ),
        }}
        listeners={{
          tabPress: (e) => {
            if (handleTabPress("profile", "/(tabs)/profile")) {
              e.preventDefault();
            }
          },
        }}
      />
      <Tabs.Screen
        name="admin"
        options={{
          title: "Admin",
          tabBarIcon: ({ color, size }) => (
            <Ionicons name="shield-outline" size={size} color={color} />
          ),
          // Hide this tab from non-admin users
          href: isAdmin ? "/(tabs)/admin" : null,
        }}
      />
      {/* Hidden screens - accessible via navigation but not in tab bar */}
      <Tabs.Screen
        name="notifications"
        options={{
          title: "Meldingen",
          href: null, // Hide from tab bar
        }}
      />
    </Tabs>

    {/* Celebration Modal for realtime notifications */}
    <CelebrationModal
      visible={showCelebration}
      onDismiss={dismissCelebration}
      notification={currentNotification}
    />

    {/* Notifications Carousel for unread notifications on app open */}
    <NotificationsCarousel
      visible={showCarousel}
      notifications={unreadNotifications}
      onDismiss={dismissCarousel}
      onMarkAllAsRead={markAllAsRead}
      onMarkAsRead={markAsRead}
    />

    {/* User Onboarding Modal - shown for first-time users */}
    <Modal
      visible={needsOnboarding}
      animationType="slide"
      presentationStyle="fullScreen"
    >
      <OnboardingCarousel
        slides={USER_ONBOARDING_SLIDES}
        finishButtonText={USER_ONBOARDING_BUTTON_TEXT}
        onComplete={completeOnboarding}
      />
    </Modal>

    {/* Complete Profile Modal - shown after onboarding if profile is incomplete */}
    <Modal
      visible={needsProfileCompletion && !needsOnboarding}
      animationType="slide"
      presentationStyle="fullScreen"
    >
      <CompleteProfileScreen
        onComplete={async () => {
          await refreshProfile();
          await completeProfileSetup();
        }}
        onSkip={completeProfileSetup}
      />
    </Modal>
    </>
  );
}
